import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Loader2, Plus, Minus, ShoppingBag, Gift, Edit3, Cake, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import api from "../../lib/api";

/**
 * Timeline cronológico de movimientos de puntos del cliente.
 *
 * Props: open, onClose, customer (con customer_id y name)
 */
const SOURCE_LABELS = {
  scan_purchase:       { icon: ShoppingBag,    label: "Consumo (escaneado)" },
  scan_visit_only:     { icon: ShoppingBag,    label: "Visita registrada"   },
  scan_redemption:     { icon: Gift,            label: "Canje (escaneado)"   },
  purchase:            { icon: ShoppingBag,    label: "Consumo"              },
  redemption:          { icon: Gift,            label: "Canje"                },
  manual_gift:         { icon: Gift,            label: "Regalo manual"        },
  manual_compensation: { icon: Plus,            label: "Compensación"         },
  manual_correction:   { icon: Edit3,           label: "Corrección"           },
  manual_bonus:        { icon: Plus,            label: "Bonus"                },
  manual_other:        { icon: Edit3,           label: "Ajuste manual"        },
  birthday:            { icon: Cake,            label: "Cumpleaños"           },
};

function relativeDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Hace un momento";
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH} h`;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function CustomerTransactionsDialog({ open, onClose, customer }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!open || !customer?.customer_id) return;
    setLoading(true);
    api.listCustomerTransactions(customer.customer_id, { limit: 100 })
      .then(r => { setItems(r.items || []); setTotal(r.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [open, customer?.customer_id]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="customer-transactions-dialog">
        <DialogHeader>
          <DialogTitle>Historial de puntos — {customer?.name}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-1.5 text-sm">
              <span>{total} movimiento{total === 1 ? "" : "s"} · Saldo actual</span>
              <Badge variant="secondary">{customer?.points || 0} pts</Badge>
            </div>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            Sin movimientos registrados todavía.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6 space-y-2.5" data-testid="tx-list">
            {items.map((t) => {
              const meta = SOURCE_LABELS[t.source] || { icon: Edit3, label: t.source };
              const Icon = meta.icon;
              const positive = (t.points_delta || 0) >= 0;
              return (
                <div key={t.transaction_id} className="flex items-start gap-3 rounded-xl border border-border p-3" data-testid={`tx-row-${t.transaction_id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${positive ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{meta.label}</p>
                      <p className={`font-mono font-semibold text-sm ${positive ? "text-emerald-700" : "text-rose-700"}`}>
                        {positive ? "+" : ""}{t.points_delta} pts
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>{relativeDate(t.created_at)}</span>
                      {t.amount > 0 && (
                        <>
                          <span>·</span>
                          <span>{new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(t.amount)}</span>
                        </>
                      )}
                    </div>
                    {t.notes && <p className="text-xs text-muted-foreground mt-1 italic">“{t.notes}”</p>}
                  </div>
                  {positive ? <ArrowUpCircle className="w-4 h-4 text-emerald-500 shrink-0 opacity-60 mt-1" /> : <ArrowDownCircle className="w-4 h-4 text-rose-500 shrink-0 opacity-60 mt-1" />}
                </div>
              );
            })}
            {total > items.length && (
              <p className="text-center text-xs text-muted-foreground pt-3 border-t">
                Mostrando los últimos {items.length} de {total} movimientos
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
